/**
 * useIntegrationsHealth — Iter-220g
 *
 * Lightweight hook for surfacing integration-status badges on the
 * admin Dashboard + Home Quick Actions. Polls two existing endpoints
 * (no new backend required):
 *   • GET /integrations/xero/status         → Xero connection state
 *   • GET /background-checks/status         → Yardstik (BG check) state
 *
 * Returns a compact roll-up:
 *   • xero:     { ok, needsReauth, configured, expired, lastError }
 *   • yardstik: { ok, enabled, mockMode, provider, lastError }
 *   • alertCount     — number of integrations in a non-OK state
 *   • worstSeverity  — 'ok' | 'warn' | 'error' (drives the badge tone)
 *   • loading        — true on first fetch
 *
 * Failures fetching the status endpoints themselves are treated as
 * silent — surface no badge rather than a misleading "error" badge
 * (the admin doesn't need a UI fight just to navigate to the page).
 */
import { useEffect, useState } from 'react';
import api from '../utils/api';

const initial = {
  xero: { ok: true, needsReauth: false, configured: false, expired: false, lastError: null },
  yardstik: { ok: true, enabled: false, mockMode: false, provider: null, lastError: null },
  alertCount: 0,
  worstSeverity: 'ok',
  loading: true,
};

export const useIntegrationsHealth = () => {
  const [state, setState] = useState(initial);

  useEffect(() => {
    let alive = true;
    (async () => {
      const next = { ...initial, loading: false };

      // Xero
      try {
        const r = await api.get('/integrations/xero/status');
        const d = r?.data || {};
        const needsReauth = !!(d.needs_reauth || (d.configured && d.expired));
        next.xero = {
          ok: !needsReauth,
          needsReauth,
          configured: !!d.configured,
          expired: !!d.expired,
          lastError: d.last_refresh_error || null,
        };
      } catch (_e) {
        // Endpoint failed — don't show a false alarm.
        next.xero = { ...initial.xero };
      }

      // Yardstik
      try {
        const r = await api.get('/background-checks/status');
        const d = r?.data || {};
        // "OK" = enabled AND not in mock mode (mock = sandboxed, no
        // real candidate writes — admins should know about it).
        const ok = !!d.enabled && !d.mock_mode;
        next.yardstik = {
          ok,
          enabled: !!d.enabled,
          mockMode: !!d.mock_mode,
          provider: d.provider || null,
          lastError: d.last_error || null,
        };
      } catch (_e) {
        next.yardstik = { ...initial.yardstik };
      }

      const alerts = [];
      if (next.xero.needsReauth) alerts.push('xero-reauth');
      if (!next.yardstik.ok && next.yardstik.enabled === false) alerts.push('yardstik-off');
      if (next.yardstik.mockMode) alerts.push('yardstik-mock');
      next.alertCount = alerts.length;
      // Severity ladder — needsReauth is the only true blocker today.
      next.worstSeverity = next.xero.needsReauth
        ? 'warn'
        : next.yardstik.mockMode || !next.yardstik.enabled
          ? 'info'
          : 'ok';

      if (alive) setState(next);
    })();
    return () => { alive = false; };
  }, []);

  return state;
};

export default useIntegrationsHealth;
